import { useEffect, useMemo, useRef, useState } from 'react';
import { endOfDay, format, parseISO, startOfDay, subDays } from 'date-fns';
import {
  CalendarDays,
  Check,
  CornerDownLeft,
  History,
  Loader2,
  Paperclip,
  Search,
  SearchX,
  User,
  X,
} from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Avatar } from '@/components/common/Avatar';
import { cn } from '@/lib/cn';
import { getErrorMessage } from '@/lib/getErrorMessage';
import type {
  ChannelData,
  ChatUser,
  MessageSearchResult,
  MessageSearchScope,
} from '@/api/services/channels.service';
import { useMessageSearch } from '../hooks/useChannels';
import { fullName, searchTerms } from '../utils/messageResults';
import { Kbd, MessageResultContent, ResultSkeleton, StatePanel } from './MessageResultParts';

// ─── Constants ────────────────────────────────────────────────────────────────
const SEARCH_DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 2;
const MAX_RECENT_SEARCHES = 5;

/** Small secondary action used in the empty, error and recent-search states. */
const QUIET_BUTTON_CLASS =
  'rounded-md border border-slate-300 px-2.5 py-1 text-[11px] font-medium text-slate-700 transition-colors hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-primary/30 dark:border-white/10 dark:text-gray-200 dark:hover:bg-white/[0.06]';

const SCOPE_OPTIONS: Array<{ value: MessageSearchScope; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'channels', label: 'Channels' },
  { value: 'direct', label: 'DMs' },
  { value: 'threads', label: 'Threads' },
];

type DatePreset = 'any' | 'today' | '7d' | '30d' | 'custom';

const DATE_PRESETS: Array<{ value: Exclude<DatePreset, 'custom'>; label: string }> = [
  { value: 'any', label: 'Any time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
];

type FilterMenu = 'sender' | 'date' | null;

// ─── Helpers ──────────────────────────────────────────────────────────────────
/** Recent searches are a per-browser convenience; storage may be unavailable. */
function readRecentSearches(storageKey: string): string[] {
  try {
    const stored: unknown = JSON.parse(localStorage.getItem(storageKey) || '[]');
    return Array.isArray(stored)
      ? stored
          .filter((item): item is string => typeof item === 'string')
          .slice(0, MAX_RECENT_SEARCHES)
      : [];
  } catch {
    return [];
  }
}

function writeRecentSearches(storageKey: string, searches: string[]): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(searches));
  } catch {
    // Recent searches simply aren't remembered when storage is unavailable.
  }
}

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timeout = window.setTimeout(() => setDebounced(value), delay);
    return () => window.clearTimeout(timeout);
  }, [value, delay]);
  return debounced;
}

function dateRange(
  preset: DatePreset,
  customFrom: string,
  customTo: string
): { from?: string; to?: string } {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { from: startOfDay(now).toISOString() };
    case '7d':
      return { from: startOfDay(subDays(now, 6)).toISOString() };
    case '30d':
      return { from: startOfDay(subDays(now, 29)).toISOString() };
    case 'custom':
      return {
        from: customFrom ? startOfDay(parseISO(customFrom)).toISOString() : undefined,
        to: customTo ? endOfDay(parseISO(customTo)).toISOString() : undefined,
      };
    default:
      return {};
  }
}

function dateLabel(preset: DatePreset, customFrom: string, customTo: string): string {
  if (preset !== 'custom') return DATE_PRESETS.find((option) => option.value === preset)!.label;
  const short = (value: string) => format(parseISO(value), 'MMM d');
  if (customFrom && customTo) return `${short(customFrom)} – ${short(customTo)}`;
  if (customFrom) return `After ${short(customFrom)}`;
  if (customTo) return `Before ${short(customTo)}`;
  return 'Custom range';
}

// ─── Small building blocks ────────────────────────────────────────────────────
function FilterChip({
  active,
  open,
  icon: Icon,
  children,
  onClick,
  ...props
}: {
  active: boolean;
  open?: boolean;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  onClick: () => void;
} & Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'>): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex h-7 max-w-[11rem] items-center gap-1.5 rounded-md border px-2 text-[11px] font-medium transition-colors',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30',
        active
          ? 'border-primary/30 bg-primary/5 text-primary dark:border-primary-light/30 dark:bg-primary-light/10 dark:text-primary-light'
          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 dark:border-white/10 dark:bg-white/[0.04] dark:text-gray-300 dark:hover:bg-white/[0.08] dark:hover:text-white',
        open && !active && 'bg-slate-50 text-slate-900 dark:bg-white/[0.08] dark:text-white'
      )}
      {...props}
    >
      <Icon className="h-3.5 w-3.5 shrink-0" />
      <span className="truncate">{children}</span>
    </button>
  );
}

function MenuOption({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] text-slate-700 transition-colors hover:bg-slate-100 focus-visible:bg-slate-100 dark:text-gray-200 dark:hover:bg-white/[0.06] dark:focus-visible:bg-white/[0.06]"
    >
      <span className="flex min-w-0 flex-1 items-center gap-2">{children}</span>
      {selected && <Check className="h-3.5 w-3.5 shrink-0 text-primary dark:text-primary-light" />}
    </button>
  );
}

const MENU_PANEL_CLASS =
  'absolute left-0 top-[calc(100%+4px)] z-20 rounded-lg border border-slate-200 bg-white p-1 shadow-lg animate-in fade-in zoom-in-95 duration-100 dark:border-white/10 dark:bg-gray-800';

const DATE_FIELD_CLASS =
  'h-7 w-full rounded-md border border-slate-300 bg-white px-2 text-[11px] text-slate-700 outline-none transition-colors focus:border-primary/50 focus:ring-1 focus:ring-primary/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-200 dark:[color-scheme:dark]';

// ─── Dialog ───────────────────────────────────────────────────────────────────
export interface MessageSearchDialogProps {
  open: boolean;
  onClose: () => void;
  channels: ChannelData[];
  /** Candidates for the sender filter. */
  people: ChatUser[];
  /** Scopes the remembered recent searches to the signed-in user. */
  userId?: string;
  onSelect: (result: MessageSearchResult) => void;
}

/**
 * Search across every channel and DM the user can read. Filter state lives
 * here (not in the body) so reopening after a jump shows the same results.
 */
export function MessageSearchDialog({
  open,
  onClose,
  channels,
  people,
  userId,
  onSelect,
}: MessageSearchDialogProps): React.ReactElement {
  const recentStorageKey = `kaamco_message_search_recent_${userId ?? 'anonymous'}`;
  const [query, setQuery] = useState('');
  // Keyed by user so a sign-in change (or the profile loading late) re-reads storage.
  const [recent, setRecent] = useState(() => ({
    key: recentStorageKey,
    searches: readRecentSearches(recentStorageKey),
  }));
  const recentSearches =
    recent.key === recentStorageKey ? recent.searches : readRecentSearches(recentStorageKey);
  const [scope, setScope] = useState<MessageSearchScope>('all');
  const [sender, setSender] = useState<ChatUser | null>(null);
  const [datePreset, setDatePreset] = useState<DatePreset>('any');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [hasAttachments, setHasAttachments] = useState(false);
  const [openMenu, setOpenMenu] = useState<FilterMenu>(null);
  const [senderQuery, setSenderQuery] = useState('');
  // Tied to the result list it was set for, so a new search starts at the top.
  const [highlight, setHighlight] = useState<{ results: unknown; index: number }>({
    results: null,
    index: 0,
  });

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const filtersRef = useRef<HTMLDivElement>(null);

  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const range = useMemo(
    () => dateRange(datePreset, customFrom, customTo),
    [datePreset, customFrom, customTo]
  );
  const search = useMessageSearch({
    q: debouncedQuery,
    scope,
    senderId: sender?._id,
    ...range,
    hasAttachments,
  });

  const results = useMemo(() => search.data ?? [], [search.data]);
  const terms = useMemo(() => searchTerms(debouncedQuery), [debouncedQuery]);
  const channelById = useMemo(
    () => new Map(channels.map((channel) => [channel._id, channel])),
    [channels]
  );
  const filteredPeople = useMemo(() => {
    const q = senderQuery.trim().toLowerCase();
    const sorted = [...people].sort((a, b) => fullName(a).localeCompare(fullName(b)));
    if (!q) return sorted;
    return sorted.filter(
      (person) =>
        fullName(person).toLowerCase().includes(q) || person.email.toLowerCase().includes(q)
    );
  }, [people, senderQuery]);

  const filtersActive =
    scope !== 'all' || Boolean(sender) || datePreset !== 'any' || hasAttachments;
  const queryTooShort = query.trim().length < MIN_QUERY_LENGTH;
  const isSettling = query.trim() !== debouncedQuery;
  const activeIndex =
    highlight.results === results && results.length > 0
      ? Math.min(highlight.index, results.length - 1)
      : 0;
  const setHighlighted = (index: number) => setHighlight({ results, index });

  // Re-opening keeps the last search; select it so typing replaces it.
  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => inputRef.current?.select());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const updateRecentSearches = (next: string[]) => {
    setRecent({ key: recentStorageKey, searches: next });
    writeRecentSearches(recentStorageKey, next);
  };

  useEffect(() => {
    listRef.current
      ?.querySelector<HTMLElement>('[data-active="true"]')
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // Close a filter menu on any click outside the filter row.
  useEffect(() => {
    if (!openMenu) return;
    const close = (event: MouseEvent) => {
      if (!filtersRef.current?.contains(event.target as Node)) setOpenMenu(null);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openMenu]);

  const clearFilters = () => {
    setScope('all');
    setSender(null);
    setDatePreset('any');
    setCustomFrom('');
    setCustomTo('');
    setHasAttachments(false);
    setOpenMenu(null);
  };

  const choose = (result: MessageSearchResult | undefined) => {
    if (!result) return;
    // Only searches that led somewhere are worth offering again.
    const term = debouncedQuery.toLowerCase();
    updateRecentSearches(
      [debouncedQuery, ...recentSearches.filter((recent) => recent.toLowerCase() !== term)].slice(
        0,
        MAX_RECENT_SEARCHES
      )
    );
    onSelect(result);
  };

  const applyRecentSearch = (recent: string) => {
    setQuery(recent);
    inputRef.current?.focus();
  };

  const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (results.length) setHighlighted((activeIndex + 1) % results.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (results.length) setHighlighted((activeIndex - 1 + results.length) % results.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      choose(results[activeIndex]);
    }
  };

  const renderBody = (): React.ReactNode => {
    if (!query.trim() && recentSearches.length > 0) {
      return (
        <div className="px-1.5 py-1.5">
          <div className="flex items-center justify-between px-2.5 pb-1 pt-1">
            <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-slate-400 dark:text-gray-500">
              Recent searches
            </p>
            <button
              type="button"
              onClick={() => updateRecentSearches([])}
              className="rounded px-1 text-[11px] text-slate-400 transition-colors hover:text-slate-700 focus-visible:text-slate-700 dark:text-gray-500 dark:hover:text-gray-200"
            >
              Clear
            </button>
          </div>
          {recentSearches.map((recent) => (
            <div
              key={recent}
              className="group/recent flex items-center rounded-md transition-colors hover:bg-slate-50 dark:hover:bg-white/[0.03]"
            >
              <button
                type="button"
                onClick={() => applyRecentSearch(recent)}
                className="flex min-w-0 flex-1 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-[12px] text-slate-700 focus-visible:bg-slate-100 dark:text-gray-200 dark:focus-visible:bg-white/[0.06]"
              >
                <History className="h-3.5 w-3.5 shrink-0 text-slate-400 dark:text-gray-500" />
                <span className="truncate">{recent}</span>
              </button>
              <button
                type="button"
                onClick={() =>
                  updateRecentSearches(recentSearches.filter((item) => item !== recent))
                }
                className="mr-1 rounded p-1 text-slate-400 opacity-0 transition-opacity hover:text-slate-700 focus-visible:opacity-100 group-hover/recent:opacity-100 dark:hover:text-gray-200 max-sm:opacity-100"
                aria-label={`Remove “${recent}” from recent searches`}
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      );
    }
    if (queryTooShort) {
      return (
        <StatePanel icon={Search} title="Search old messages">
          {query.trim().length === 1 ? (
            'Keep typing — searches need at least 2 characters.'
          ) : (
            <>
              Find messages, files and voice notes across your channels and direct messages. Use
              quotes for an exact phrase.
            </>
          )}
        </StatePanel>
      );
    }
    if (search.isError) {
      return (
        <StatePanel icon={SearchX} title="Couldn’t search messages">
          <p>{getErrorMessage(search.error)}</p>
          <button
            type="button"
            onClick={() => void search.refetch()}
            className={cn(QUIET_BUTTON_CLASS, 'mt-3')}
          >
            Try again
          </button>
        </StatePanel>
      );
    }
    if (search.isLoading || (isSettling && results.length === 0)) return <ResultSkeleton />;
    if (results.length === 0) {
      return (
        <StatePanel icon={SearchX} title="No matching messages">
          <p>
            Nothing matches “{debouncedQuery}”
            {filtersActive ? ' with the current filters.' : '. Try different words.'}
          </p>
          {filtersActive && (
            <button type="button" onClick={clearFilters} className={cn(QUIET_BUTTON_CLASS, 'mt-3')}>
              Clear filters
            </button>
          )}
        </StatePanel>
      );
    }
    return (
      <div
        role="listbox"
        id="message-search-results"
        aria-label="Search results"
        className={cn('px-1.5 py-1 transition-opacity', search.isFetching && 'opacity-60')}
      >
        {results.map((result, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={result._id}
              id={`message-search-result-${index}`}
              type="button"
              role="option"
              aria-selected={isActive}
              data-active={isActive}
              onClick={() => choose(result)}
              onMouseMove={() => !isActive && setHighlighted(index)}
              className={cn(
                'group/result flex w-full gap-3 rounded-md px-2.5 py-2 text-left transition-colors',
                isActive
                  ? 'bg-slate-100 dark:bg-white/[0.06]'
                  : 'hover:bg-slate-50 focus-visible:bg-slate-100 dark:hover:bg-white/[0.03] dark:focus-visible:bg-white/[0.06]'
              )}
            >
              <MessageResultContent
                message={result}
                channel={channelById.get(result.channelId)}
                terms={terms}
              />
              <CornerDownLeft
                aria-hidden
                className={cn(
                  'mt-1 hidden h-3.5 w-3.5 shrink-0 text-slate-400 sm:block',
                  isActive ? 'opacity-100' : 'opacity-0'
                )}
              />
            </button>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent
        data-message-search=""
        showCloseButton={false}
        aria-describedby={undefined}
        onEscapeKeyDown={(event) => {
          // Escape closes an open filter menu first, then the dialog. It must not
          // also reach the Channels full-screen shortcut listening on window.
          if (openMenu) {
            event.preventDefault();
            setOpenMenu(null);
          }
          event.stopPropagation();
        }}
        className={cn(
          'flex flex-col sm:top-[10%] sm:max-w-2xl sm:translate-y-0',
          // Match the Channels surfaces rather than the generic dialog grey.
          'border-slate-200 dark:border-white/10 dark:bg-gray-900',
          'max-sm:inset-0 max-sm:h-[100dvh] max-sm:max-h-none max-sm:w-full max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-none max-sm:border-0',
          'sm:max-h-[min(80dvh,640px)]'
        )}
      >
        <DialogTitle className="sr-only">Search messages</DialogTitle>

        {/* Query */}
        <div className="flex shrink-0 items-center gap-2.5 border-b border-slate-200 px-4 dark:border-white/10">
          {search.isFetching && !queryTooShort ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" aria-hidden />
          ) : (
            <Search className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
          )}
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleInputKeyDown}
            type="text"
            role="combobox"
            aria-expanded={results.length > 0}
            aria-controls="message-search-results"
            aria-activedescendant={
              results.length > 0 ? `message-search-result-${activeIndex}` : undefined
            }
            aria-label="Search messages"
            placeholder="Search messages…"
            autoComplete="off"
            spellCheck={false}
            className="h-12 min-w-0 flex-1 bg-transparent text-[14px] text-slate-900 placeholder:text-slate-400 focus:outline-none dark:text-gray-100 dark:placeholder:text-gray-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => {
                setQuery('');
                inputRef.current?.focus();
              }}
              className="rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:bg-slate-100 dark:hover:bg-white/10 dark:hover:text-gray-200 dark:focus-visible:bg-white/10"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-1.5 py-1 text-[12px] font-medium text-slate-500 hover:text-slate-800 focus-visible:text-slate-800 dark:text-gray-400 dark:hover:text-white sm:hidden"
          >
            Cancel
          </button>
          <span className="hidden sm:inline-flex">
            <Kbd>esc</Kbd>
          </span>
        </div>

        {/* Filters */}
        <div
          ref={filtersRef}
          className="flex shrink-0 flex-wrap items-center gap-1.5 border-b border-slate-200 bg-slate-50/60 px-3 py-2 dark:border-white/10 dark:bg-white/[0.02]"
        >
          <SegmentedControl
            ariaLabel="Conversation type"
            size="sm"
            options={SCOPE_OPTIONS}
            value={scope}
            onChange={setScope}
            className="max-sm:w-full [&>button]:text-[11px]"
          />
          <span
            aria-hidden
            className="mx-0.5 hidden h-4 w-px bg-slate-200 dark:bg-white/10 sm:block"
          />

          <div className="relative">
            <FilterChip
              icon={User}
              active={Boolean(sender)}
              open={openMenu === 'sender'}
              aria-haspopup="menu"
              aria-expanded={openMenu === 'sender'}
              onClick={() => {
                setSenderQuery('');
                setOpenMenu((current) => (current === 'sender' ? null : 'sender'));
              }}
            >
              {sender ? fullName(sender) : 'From anyone'}
            </FilterChip>
            {openMenu === 'sender' && (
              <div role="menu" className={cn(MENU_PANEL_CLASS, 'w-60')}>
                <div className="relative p-1">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                  <input
                    autoFocus
                    value={senderQuery}
                    onChange={(event) => setSenderQuery(event.target.value)}
                    placeholder="Find a person…"
                    aria-label="Find a person"
                    className="h-7 w-full rounded-md border border-slate-300 bg-white pl-7 pr-2 text-[12px] text-slate-700 placeholder:text-slate-400 outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20 dark:border-white/10 dark:bg-white/[0.06] dark:text-gray-200 dark:placeholder:text-gray-500"
                  />
                </div>
                <div className="max-h-56 overflow-y-auto chat-scroll">
                  {!senderQuery && (
                    <MenuOption
                      selected={!sender}
                      onClick={() => {
                        setSender(null);
                        setOpenMenu(null);
                      }}
                    >
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-white/[0.06]">
                        <User className="h-3 w-3" />
                      </span>
                      Anyone
                    </MenuOption>
                  )}
                  {filteredPeople.map((person) => (
                    <MenuOption
                      key={person._id}
                      selected={sender?._id === person._id}
                      onClick={() => {
                        setSender(person);
                        setOpenMenu(null);
                      }}
                    >
                      <Avatar name={fullName(person)} src={person.avatar} size="xs" />
                      <span className="truncate">{fullName(person)}</span>
                    </MenuOption>
                  ))}
                  {filteredPeople.length === 0 && (
                    <p className="px-2 py-3 text-center text-[11px] text-slate-400">
                      No people match “{senderQuery}”
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="relative">
            <FilterChip
              icon={CalendarDays}
              active={datePreset !== 'any'}
              open={openMenu === 'date'}
              aria-haspopup="menu"
              aria-expanded={openMenu === 'date'}
              onClick={() => setOpenMenu((current) => (current === 'date' ? null : 'date'))}
            >
              {dateLabel(datePreset, customFrom, customTo)}
            </FilterChip>
            {openMenu === 'date' && (
              <div role="menu" className={cn(MENU_PANEL_CLASS, 'w-56 max-sm:-left-24')}>
                {DATE_PRESETS.map((option) => (
                  <MenuOption
                    key={option.value}
                    selected={datePreset === option.value}
                    onClick={() => {
                      setDatePreset(option.value);
                      setOpenMenu(null);
                    }}
                  >
                    {option.label}
                  </MenuOption>
                ))}
                <div className="my-1 h-px bg-slate-100 dark:bg-white/10" />
                <MenuOption
                  selected={datePreset === 'custom'}
                  onClick={() => setDatePreset('custom')}
                >
                  Custom range
                </MenuOption>
                {datePreset === 'custom' && (
                  <div className="grid grid-cols-2 gap-1.5 px-2 pb-1.5 pt-1">
                    <label className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                      From
                      <input
                        type="date"
                        value={customFrom}
                        max={customTo || undefined}
                        onChange={(event) => setCustomFrom(event.target.value)}
                        className={cn(DATE_FIELD_CLASS, 'mt-0.5')}
                      />
                    </label>
                    <label className="text-[10px] font-medium text-slate-500 dark:text-gray-400">
                      To
                      <input
                        type="date"
                        value={customTo}
                        min={customFrom || undefined}
                        onChange={(event) => setCustomTo(event.target.value)}
                        className={cn(DATE_FIELD_CLASS, 'mt-0.5')}
                      />
                    </label>
                  </div>
                )}
              </div>
            )}
          </div>

          <FilterChip
            icon={Paperclip}
            active={hasAttachments}
            aria-pressed={hasAttachments}
            onClick={() => setHasAttachments((current) => !current)}
          >
            Has attachments
          </FilterChip>

          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="ml-auto rounded-md px-1.5 py-1 text-[11px] font-medium text-slate-500 transition-colors hover:text-slate-900 dark:text-gray-400 dark:hover:text-white"
            >
              Clear
            </button>
          )}
        </div>

        {/* Results */}
        <div ref={listRef} className="min-h-0 flex-1 overflow-y-auto chat-scroll">
          {renderBody()}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center gap-4 border-t border-slate-200 px-4 py-2 text-[11px] text-slate-400 dark:border-white/10 dark:text-gray-500">
          <span>
            {!queryTooShort && !search.isLoading && !search.isError
              ? results.length >= 50
                ? 'Showing the 50 best matches'
                : `${results.length} ${results.length === 1 ? 'result' : 'results'}`
              : 'Searches channels and DMs you can access'}
          </span>
          <span className="ml-auto hidden items-center gap-1 sm:flex">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd>
            navigate
          </span>
          <span className="hidden items-center gap-1 sm:flex">
            <Kbd>↵</Kbd>
            open
          </span>
        </div>
      </DialogContent>
    </Dialog>
  );
}

MessageSearchDialog.displayName = 'MessageSearchDialog';
